import { useState, useEffect } from "react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useGlossaryBlueprintsDataContext } from "@/providers/GlossaryBlueprintsDataProvider";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { useGlossaryData } from "@/lib/useObjectsData";
import { useToast } from "@/hooks/use-toast";
import { checkMigrationNeeded, populateCodeIds, MigrationProgress } from "@/lib/migrations/populateCodeIds";
import { NamingConvention, ExportTemplatesConfig } from "@/types/exportTemplates";

import { CompleteExportSection } from "./CompleteExportSection";
import { DataPackageExportSection } from "./DataPackageExportSection";
import { IndividualExportsSection } from "./IndividualExportsSection";
import { ExportFormatInfoSection } from "./ExportFormatInfoSection";
import { CodeIdMigrationSection } from "./CodeIdMigrationSection";
import { ExportVersionsSection } from "./ExportVersionsSection";
import { SaveVersionDialog } from "./SaveVersionDialog";

import { useCompleteExport } from "./useCompleteExport";
import { useIndividualExports } from "./useIndividualExports";
import { useDataPackageExport } from "./useDataPackageExport";
import { isDatabaseExport, getActiveTemplate } from "./utils";

/**
 * Import/Export Settings Section
 * 
 * Allows users to download all game data (objects, templates, texts, documents, and relationships)
 * in a format that can be used standalone in their game engine.
 */
export default function ImportExportSection() {
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  
  // Data Package Export state
  const [dataPackageMode, setDataPackageMode] = useState<'all' | 'blueprint' | 'selection'>('all');
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string>('');
  const [namingConvention, setNamingConvention] = useState<NamingConvention>('original');
  
  // Code ID Migration state
  const [codeIdMigrationCount, setCodeIdMigrationCount] = useState<number>(0);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState<MigrationProgress | null>(null);
  
  // Get all items, blueprints, types, and tags for database exports
  const { items: allItems } = useGlossaryData({
    gameId: selectedGame?.id,
    type: 'library',
    autoLoad: true
  });
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { types } = useTypesDataContext();
  const { tags } = useTagsDataContext();

  // Custom hooks for export functionality
  const { isExporting, exportProgress, handleExportAll } = useCompleteExport();
  const {
    isExporting: isExportingIndividual,
    handleExportObjects,
    handleExportTemplates,
    handleExportTexts,
    handleExportDocuments
  } = useIndividualExports();
  const {
    isExportingPackage,
    isSavingToCloud,
    handleDataPackageExport,
    handleSaveToCloud,
    getActiveTemplate: getActiveTemplateFromHook,
    pendingExport,
    showSaveDialog,
    closeSaveDialog,
  } = useDataPackageExport({
    glossaryBlueprints: glossaryBlueprints || [],
    types,
    tags,
    allItems
  });

  // Check migration status when game changes
  useEffect(() => {
    if (selectedGame?.id) {
      checkMigrationNeeded(selectedGame.id)
        .then(count => setCodeIdMigrationCount(count))
        .catch(error => console.error('Error checking migration status:', error));
    }
  }, [selectedGame?.id]);

  // Handle Code ID migration
  const handleCodeIdMigration = async () => {
    if (!selectedGame) return;

    setIsMigrating(true);
    setMigrationProgress(null);

    try {
      const result = await populateCodeIds(
        selectedGame.id,
        (progress) => setMigrationProgress(progress)
      );

      if (result.success) {
        toast({
          title: "Migration Complete",
          description: `Successfully generated code IDs for ${result.progress.successful} objects`,
        });
        setCodeIdMigrationCount(0);
      } else {
        toast({
          title: "Migration Completed with Errors",
          description: `${result.progress.successful} successful, ${result.progress.failed} failed`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast({
        title: "Migration Failed",
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: "destructive",
      });
    } finally {
      setIsMigrating(false);
      setMigrationProgress(null);
    }
  };

  const handleNavigateToSettings = () => {
    window.location.hash = '';
    const event = new CustomEvent('navigate-settings', { detail: 'export-templates' });
    window.dispatchEvent(event);
  };

  // Get active template for conditional rendering
  const exportConfig = selectedGame?.settings?.exportTemplates as ExportTemplatesConfig | undefined;
  const activeTemplate = getActiveTemplate(exportConfig);
  const showIndividualExports = !isDatabaseExport(activeTemplate);

  return (
    <div className="max-w-4xl space-y-8">
      {!selectedGame && (
        <div className="p-4 border border-warning bg-warning/10 rounded">
          <p className="text-warning-foreground">
            Please select a game to export data
          </p>
        </div>
      )}

      {selectedGame && (
        <>
          <CompleteExportSection
            onExport={() => handleExportAll(selectedGame)}
            isExporting={isExporting}
            exportProgress={exportProgress}
          />

          <DataPackageExportSection
            gameId={selectedGame.id}
            activeTemplate={activeTemplate}
            dataPackageMode={dataPackageMode}
            selectedBlueprintId={selectedBlueprintId}
            namingConvention={namingConvention}
            isExporting={isExporting || isExportingIndividual}
            isExportingPackage={isExportingPackage}
            isSavingToCloud={isSavingToCloud}
            onModeChange={setDataPackageMode}
            onBlueprintChange={setSelectedBlueprintId}
            onNamingConventionChange={setNamingConvention}
            onExport={() => handleDataPackageExport(
              selectedGame,
              dataPackageMode,
              selectedBlueprintId,
              namingConvention
            )}
            onSaveToCloud={() => handleSaveToCloud(
              selectedGame,
              dataPackageMode,
              selectedBlueprintId,
              namingConvention
            )}
            onNavigateToSettings={handleNavigateToSettings}
          />

          {showIndividualExports && (
            <IndividualExportsSection
              isExporting={isExportingIndividual || isExporting}
              onExportObjects={() => handleExportObjects(selectedGame)}
              onExportTemplates={() => handleExportTemplates(selectedGame)}
              onExportTexts={() => handleExportTexts(selectedGame)}
              onExportDocuments={() => handleExportDocuments(selectedGame)}
            />
          )}

          {showIndividualExports && <ExportFormatInfoSection />}

          <ExportVersionsSection gameId={selectedGame.id} />

          <CodeIdMigrationSection
            migrationCount={codeIdMigrationCount}
            isMigrating={isMigrating}
            migrationProgress={migrationProgress}
            onMigrate={handleCodeIdMigration}
          />
        </>
      )}

      {/* Save Version Dialog */}
      {pendingExport && selectedGame && (
        <SaveVersionDialog
          open={showSaveDialog}
          onOpenChange={(open) => {
            if (!open) {
              closeSaveDialog();
            }
          }}
          exportBlob={pendingExport.blob}
          exportFilename={pendingExport.filename}
          gameId={selectedGame.id}
          gameName={selectedGame.name}
          companyId={selectedGame.company_id}
          platform={pendingExport.platform}
          templateName={pendingExport.templateName}
          metadata={pendingExport.metadata}
          onSaved={closeSaveDialog}
        />
      )}
    </div>
  );
}
